import {
  Info,
  Home,
  Megaphone,
  Calendar,
  Building2,
  MapPin,
  ShoppingCart,
  Utensils,
  Search,
  ShoppingBag,
  Wrench,
  HandHeart,
  AlertCircle,
  HelpCircle,
  Gift,
  MessageCircleQuestion,
  BookOpen,
  Shirt,
  GraduationCap,
  UtensilsCrossed,
  Car,
  Home as HomeIcon,
  HandHelping,
  Briefcase,
  FileText,
  ClipboardList,
  CalendarClock,
  ShieldAlert,
  UserCheck,
  Sparkles,
} from "lucide-react";
import { LayoutConfig } from "../../../components/layout/types";

export const dashboardLayoutConfig: LayoutConfig = {
  brandName: "Gedoba",
  brandSubtitle: "Building Portal",
  sidebarWidth: 256, // 64 * 4 = 256px (w-64 equivalent)
  headerHeight: "4rem",
  showLocationDropdown: true,
  showSearchBar: true,
  searchPlaceholder: "Explore...",
  showViewSwitcher: true,
  showViewModeToggle: true,
  menuSections: [
    {
      label: "",
      defaultOpen: true,
      items: [
        {
          icon: Info,
          label: "About",
          path: "/dashboard",
          color: "text-slate-600",
        },
        {
          icon: Building2,
          label: "My buildings",
          path: "/my-buildings",
          color: "text-sky-700",
        },
        {
          icon: Megaphone,
          label: "Notices",
          path: "/notices",
          color: "text-purple-600",
          sectionKey: "showNotice",
        },
        {
          icon: Calendar,
          label: "Events/Activities",
          path: "/events",
          color: "text-blue-500",
          sectionKey: "showEvent",
        },
      ],
    },
    {
      label: "Support & Safety",
      defaultOpen: true,
      // Education: Student tab · Skyscraper: Community tab
      viewModes: ["student_life", "community"],
      items: [
        {
          icon: AlertCircle,
          label: "Time Sensitive Help",
          path: "/immediate-help",
          color: "text-red-600",
          sectionKey: "showUrgentHelp",
        },
        {
          icon: HelpCircle,
          label: "Ask for Help",
          path: "/ask-for-help",
          color: "text-blue-600",
          sectionKey: "showAskForHelp",
        },
        {
          icon: Sparkles,
          label: "Cleaning & Upkeep",
          path: "/upkeep",
          color: "text-emerald-600",
          sectionKey: "showUpkeepRequest",
        },
      ],
    },
    {
      label: "Marketplace",
      defaultOpen: true,
      // Education: Marketplace · Skyscraper: Business (professional listings)
      viewModes: ["marketplace", "business"],
      items: [
        {
          icon: ShoppingBag,
          label: "Businesses",
          path: "/retail",
          color: "text-pink-600",
          sectionKey: "showRetail",
        },
      ],
    },
    {
      label: "Student Exchange",
      defaultOpen: true,
      // Education: Student · Skyscraper: Community (peer buy/sell + home business)
      viewModes: ["student_life", "community"],
      items: [
        {
          icon: ShoppingCart,
          label: "Buy & Sell",
          path: "/buy-sell",
          color: "text-green-600",
          sectionKey: "showBuySell",
        },
        {
          icon: HandHeart,
          label: "Request & Offer",
          path: "/request-offer",
          color: "text-indigo-600",
          sectionKey: "showRequestOffer",
        },
        {
          icon: HomeIcon,
          label: "Accommodation",
          path: "/accommodation",
          color: "text-cyan-600",
          sectionKey: "showAccommodation",
        },
      ],
    },
    {
      label: "Community",
      defaultOpen: true,
      viewModes: ["student_life", "community"],
      items: [
        {
          icon: Search,
          label: "Lost & Found",
          path: "/lost-found",
          color: "text-blue-500",
          sectionKey: "showLostFound",
        },
        {
          icon: Car,
          label: "Car Pooling",
          path: "/carpooling",
          color: "text-blue-500",
          sectionKey: "showService",
        },
        {
          icon: Gift,
          label: "Share with Neighbors",
          path: "/share-with-neighbors",
          color: "text-green-600",
          sectionKey: "showShareWithNeighbors",
        },
        {
          icon: MessageCircleQuestion,
          label: "Other Requests",
          path: "/other-requests",
          color: "text-amber-600",
          sectionKey: "showOtherRequests",
        },
      ],
    },
    {
      label: "Academics",
      defaultOpen: true,
      viewModes: ["academics"],
      items: [
        {
          icon: GraduationCap,
          label: "Tutor Help",
          path: "/services?category=TUTORING",
          color: "text-purple-600",
          sectionKey: "showService",
        },
        {
          icon: BookOpen,
          label: "Textbooks",
          path: "/textbooks",
          color: "text-blue-600",
          sectionKey: "showBuySell",
        },
      ],
    },
    {
      label: "Campus Life",
      defaultOpen: true,
      // Education: Campus Life · Skyscraper: Management / building ops
      viewModes: ["campus", "management"],
      items: [
        {
          icon: Building2,
          label: "Tenants",
          path: "/tenants",
          color: "text-cyan-600",
          sectionKey: "showTenantBusiness",
        },
        {
          icon: Utensils,
          label: "Food Court",
          path: "/food-court",
          color: "text-orange-600",
          sectionKey: "showFoodCourt",
        },
        {
          icon: MapPin,
          label: "Facilities",
          path: "/facilities",
          color: "text-teal-600",
          sectionKey: "showFacility",
        },
        {
          icon: Wrench,
          label: "Services",
          path: "/services",
          color: "text-purple-500",
          sectionKey: "showService",
        },
        {
          icon: Briefcase,
          label: "Jobs",
          path: "/jobs",
          color: "text-emerald-500",
          sectionKey: "showJob",
        },
      ],
    },
    {
      // Pack-agnostic modules: any portal pack can enable these via section flags.
      // Shown under Campus Life (education) and Management (skyscraper/retail/…).
      label: "Building management",
      defaultOpen: true,
      viewModes: ["campus", "management"],
      items: [
        {
          icon: FileText,
          label: "Documents",
          path: "/building-documents",
          color: "text-sky-700",
          sectionKey: "showBuildingDocument",
        },
        {
          icon: ClipboardList,
          label: "Work orders",
          path: "/work-orders",
          color: "text-amber-700",
          sectionKey: "showWorkOrder",
        },
        {
          icon: Sparkles,
          label: "Cleaning & Upkeep",
          path: "/upkeep",
          color: "text-emerald-600",
          sectionKey: "showUpkeepRequest",
        },
        {
          icon: UserCheck,
          label: "Members",
          path: "/bm-access",
          color: "text-slate-700",
          requiresBuildingManager: true,
        },
        {
          icon: CalendarClock,
          label: "Ops schedule",
          path: "/building-schedule",
          color: "text-indigo-600",
          sectionKey: "showBuildingSchedule",
        },
        {
          icon: ShieldAlert,
          label: "Safety hub",
          path: "/building-safety",
          color: "text-red-600",
          sectionKey: "showBuildingSafety",
        },
      ],
    },
  ],
};
